/**
 * CP-14.6.3 — Send RFQ modal.
 *
 * Triggered from a CrewShapeSearch result card. Pre-fills the crew shape
 * from the search state and the recipient from the card. The user can:
 *   - Edit a free-text notes field (required, 10–2000 chars).
 *   - Choose an expiry override (24h / 72h default / 7d).
 *   - Submit, which posts to `POST /api/v1/rfq`. On success navigates to
 *     /app/rfq/:id.
 *
 * Rules honored (CP-14.6 contract):
 *   - Text-only. No attachments / images.
 *   - No rate edits (snapshot is server-computed; client never mutates it).
 *   - No counter-offers.
 *   - Marketplace context drives recipient_kind classification:
 *       owner_kind='org' + marketplace='workforce' -> workforce_provider
 *       owner_kind='org' + (integrator|event_tech)  -> contractor_company
 *       owner_kind='user'                           -> independent_contractor
 *   - Snapshot recipient/sender/title labels are read-only in the form
 *     (we display them but never PATCH them).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const EXPIRY_OPTIONS = [
  { value: 24, label: "24 hours" },
  { value: 72, label: "72 hours (default)" },
  { value: 168, label: "7 days" },
];

function classifyRecipientKind(card, marketplace) {
  if (card.owner_kind === "user") return "independent_contractor";
  if (marketplace === "workforce") return "workforce_provider";
  return "contractor_company";
}

export default function SendRFQModal({
  open,
  card,
  marketplace,
  requirements,
  trackSlug,
  regionCode,
  onClose,
}) {
  const nav = useNavigate();
  const [notes, setNotes] = useState("");
  const [expiry, setExpiry] = useState(72);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const recipient_kind = classifyRecipientKind(card, marketplace);

  const submit = async () => {
    setErr("");
    if (!notes.trim() || notes.trim().length < 10) {
      setErr("Please include at least 10 characters of context in the notes.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        marketplace_code: marketplace,
        recipient_kind,
        recipient_user_id: card.owner_kind === "user" ? card.owner_id : null,
        recipient_org_id: card.owner_kind === "org" ? card.owner_id : null,
        crew_requirements: requirements
          .filter((r) => r.title_code && Number(r.quantity) > 0)
          .map((r) => ({
            title_code: r.title_code,
            quantity: Number(r.quantity) || 1,
          })),
        track_slug: trackSlug || null,
        region_code: regionCode || null,
        notes: notes.trim(),
        expiry_hours: expiry,
      };
      const r = await api("/rfq", { method: "POST", body: payload });
      toast.success(`RFQ #${r.id} sent to ${card.display_name}.`);
      onClose?.();
      nav(`/app/rfq/${r.id}`);
    } catch (e) {
      setErr(e.message || "Failed to send RFQ.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={TID.rfqModal}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold">Send RFQ to {card.display_name}</h2>
          <p className="text-sm text-[var(--avcp-ink-soft)] mt-1">
            We&apos;ll snapshot the crew shape exactly as you see it now. The
            recipient can accept, decline, or message you back. No money moves.
          </p>
        </header>

        <section className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)]">
                Marketplace
              </div>
              <div className="font-medium">{marketplace}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)]">
                Recipient kind
              </div>
              <div className="font-medium">{recipient_kind}</div>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] mb-2">
              Crew shape (read-only — captured at send time)
            </div>
            <div className="rounded-lg border border-slate-200">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-slate-200">
                    <th className="p-2">Resource type</th>
                    <th className="p-2 w-20">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements
                    .filter((r) => r.title_code && Number(r.quantity) > 0)
                    .map((r, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-100 last:border-b-0"
                        data-testid={`${TID.rfqModalCrewRow}-${i}`}
                      >
                        <td className="p-2">{r.title_code}</td>
                        <td className="p-2 tabular-nums">{r.quantity}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <label className="block">
            <span className="text-sm font-medium block mb-1">
              Notes (required, 10–2000 chars)
            </span>
            <textarea
              className="avcp-input w-full min-h-[120px]"
              data-testid={TID.rfqModalNotes}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Conference keynote on April 18. 8h call. Site walk-through April 12."
              maxLength={2000}
            />
            <span className="block text-xs text-[var(--avcp-ink-soft)] mt-1">
              {notes.length} / 2000
            </span>
          </label>

          <label className="block">
            <span className="text-sm font-medium block mb-1">Expiry</span>
            <select
              className="avcp-input max-w-xs"
              data-testid={TID.rfqModalExpiry}
              value={expiry}
              onChange={(e) => setExpiry(Number(e.target.value))}
            >
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {err && (
            <div
              className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
              data-testid={TID.rfqModalError}
            >
              {err}
            </div>
          )}
        </section>

        <footer className="p-6 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            className="avcp-btn ghost"
            data-testid={TID.rfqModalCancel}
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="avcp-btn primary"
            data-testid={TID.rfqModalSubmit}
            onClick={submit}
            disabled={busy}
          >
            {busy ? "Sending…" : "Send RFQ"}
          </button>
        </footer>
      </div>
    </div>
  );
}
